import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm, useWatch } from 'react-hook-form'
import { Button, DialogActions, DialogContent } from '@material-ui/core'
import { ControlledTextField } from '../form/ControlledTextField'
import { FormGroup } from '../form/FormGroup'
import { Dialog } from '../dialog/Dialog'
import { validators } from '../../validation/validators'
import { ControlledSwitch } from '../form/ControlledSwitch'
import { ProjectVersionFragment } from '../../generated/graphql'
import { Loader } from '../common/Loader'

export type FormValues = {
  name: string
  useActiveAsBase: boolean
  useBaseVersion?: boolean
}

type Props = {
  isOpen: boolean
  defaultValues?: Partial<FormValues>
  activeVersion: ProjectVersionFragment
  onSubmit: (values: FormValues) => Promise<void>
  onClose: () => void
}

export function VersionDialog({
  isOpen,
  defaultValues,
  activeVersion,
  onSubmit,
  onClose,
}: Props): React.ReactElement {
  const { control, handleSubmit, errors } = useForm<FormValues>({
    defaultValues,
  })
  const useActiveAsBase = useWatch({ control, name: 'useActiveAsBase' })
  const { t } = useTranslation()
  const [isCreating, setIsCreating] = useState(false)

  const onFormSubmit = async (values: FormValues) => {
    setIsCreating(true)
    await onSubmit(values)
    setIsCreating(false)
  }

  return (
    <Dialog
      title={t('projectVersions.createDialogTitle')}
      open={isOpen}
      onClose={onClose}
    >
      <form onSubmit={handleSubmit(onFormSubmit)}>
        <DialogContent>
          {isCreating ? (
            <Loader />
          ) : (
            <>
              <FormGroup>
                <ControlledTextField
                  control={control}
                  name="name"
                  rules={validators.required}
                  error={errors.name}
                  type="text"
                  label={t('projectVersions.createDialogInputName')}
                />
              </FormGroup>

              <FormGroup>
                <ControlledSwitch
                  control={control}
                  name="useActiveAsBase"
                  label={t('projectVersions.createDialogInputCopyOf', {
                    name: activeVersion.name,
                  })}
                  color="primary"
                />
              </FormGroup>

              {useActiveAsBase && (
                <FormGroup>
                  <ControlledSwitch
                    control={control}
                    name="useBaseVersion"
                    label={t(
                      'projectVersions.createDialogInputOmitBaseVersion',
                    )}
                    color="primary"
                    defaultChecked
                  />
                </FormGroup>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={onClose} disabled={isCreating}>
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={isCreating}
          >
            {t('common.create')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
